import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ActivityIndicator, StyleSheet, Alert, Platform, Modal } from 'react-native';
import { useRouter } from 'expo-router';
import { observer } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import { supabase } from '../../services/supabase';
import { getURL } from '../../services/authLinks';
import { getLogger } from '../../utils/logger';
import { useWebAlert } from '../../components/WebAlert';
import FormFieldError from '../../components/FormFieldError';
import WebLoadingOverlay from '../../components/WebLoadingOverlay';
import theme from '../../config/theme';
import Constants from 'expo-constants';
import { getSignupClientMetadata } from '@/utils/platform';
import FormattedInput from '@/components/FormattedInput';
import { APP_CONSTANTS } from '@/config/constants';
import { userActions, globalState } from '@/state/globalState';
import { runDirectSignupBootstrap } from '@/services/directSignupBootstrapService';

// Logger específico para esta tela
const logger = getLogger('APP-(AUTH)-SIGNUP');
const useRedirectSignUpFlow = APP_CONSTANTS.REGISTRATION.USE_SIGNUP_REDIRECT_FLOW;
const isDirectSignUpFlow = !useRedirectSignUpFlow;

const DEFAULT_BOOTSTRAP_MESSAGE = 'Estamos configurando sua conta...';
const DEFAULT_BOOTSTRAP_HINT = 'Na primeira preparação, isso pode levar cerca de 1 minuto. Não feche o aplicativo.';

const BOOTSTRAP_VISUAL_STEPS = [
  { progress: 5, message: 'Preparando sua conta...' },
  { progress: 11, message: 'Criando o modelo de Carros...' },
  { progress: 18, message: 'Criando o modelo de Motos...' },
  { progress: 25, message: 'Criando o modelo de Caminhão Baú...' },
  { progress: 32, message: 'Criando o modelo de Cavalo mecânico...' },
  { progress: 36, message: 'Finalizando seus quatro modelos...' },
] as const;

const SignUpScreen = observer(() => {
  const router = useRouter();
  const { showAlert } = useWebAlert();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [bootstrapVisualStepIndex, setBootstrapVisualStepIndex] = useState(0);
  
  // Controles de visibilidade das senhas
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Erros de validação para cada campo
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const checkUserDatabaseDone = globalState.user.checkUserDatabaseDone.get();
  const existsInDatabase = globalState.user.existsInDatabase.get();
  const directSignupState = globalState.user.directSignup.get();
  const shouldBlockForBootstrap =
    isDirectSignUpFlow && (directSignupState.isBootstrapping || directSignupState.awaitingAppReady);
  const overlayMessage = directSignupState.overlayMessage || DEFAULT_BOOTSTRAP_MESSAGE;
  const overlayHint = directSignupState.overlayHint || DEFAULT_BOOTSTRAP_HINT;
  const bootstrapVisualStep = BOOTSTRAP_VISUAL_STEPS[bootstrapVisualStepIndex];
  const displayedOverlayMessage = directSignupState.isBootstrapping
    ? bootstrapVisualStep.message
    : overlayMessage;
  const displayedOverlayProgress = directSignupState.isBootstrapping
    ? bootstrapVisualStep.progress
    : directSignupState.awaitingAppReady
      ? 38
      : undefined;
  const displayedOverlayProgressLabel = displayedOverlayProgress === undefined
    ? undefined
    : `${displayedOverlayProgress}% concluído`;

  useEffect(() => {
    if (!directSignupState.isBootstrapping) {
      setBootstrapVisualStepIndex(0);
      return;
    }

    const intervalId = setInterval(() => {
      setBootstrapVisualStepIndex(current => (
        Math.min(current + 1, BOOTSTRAP_VISUAL_STEPS.length - 1)
      ));
    }, 2200);

    return () => clearInterval(intervalId);
  }, [directSignupState.isBootstrapping]);

  const sanitizePhoneDigits = (value: string) => value.replace(/\D/g, '');
  const getPhoneDigits = () => sanitizePhoneDigits(phone);

  const handleGoToManualRegister = () => {
    router.replace('/(auth)/register');
  };

  const updateBootstrapOverlay = (message: string, hint: string) => {
    userActions.setDirectSignupBootstrapState({
      overlayMessage: message,
      overlayHint: hint,
    });
    logger.info(`[DIRECT-SIGNUP] Overlay mensagem: "${message}"`);
    logger.info(`[DIRECT-SIGNUP] Overlay dica: "${hint}"`);
  };

  const startDirectBootstrapTracking = () => {
    userActions.resetDatabaseCheck();
    userActions.setDirectSignupBootstrapState({
      isBootstrapping: true,
      isRequired: true,
      awaitingAppReady: false,
      lastError: null,
      overlayMessage: DEFAULT_BOOTSTRAP_MESSAGE,
      overlayHint: DEFAULT_BOOTSTRAP_HINT,
    });
    logger.info('[DIRECT-SIGNUP] Bootstrap direto marcado como obrigatório; aguardando provisionamento');
    logger.info(`[DIRECT-SIGNUP] Overlay mensagem: "${DEFAULT_BOOTSTRAP_MESSAGE}"`);
    logger.info(`[DIRECT-SIGNUP] Overlay dica: "${DEFAULT_BOOTSTRAP_HINT}"`);
  };

  const finishDirectBootstrapTracking = (success: boolean, errorMessage?: string) => {
    userActions.setDirectSignupBootstrapState({
      isBootstrapping: false,
      isRequired: !success,
      awaitingAppReady: success,
      lastError: success ? null : errorMessage ?? null,
      lastCompletedAt: success ? new Date().toISOString() : null,
    });
    logger.info('[DIRECT-SIGNUP] Bootstrap direto finalizado', {
      success,
      errorMessage: errorMessage ?? null,
    });
    if (success) {
      logger.info('[DIRECT-SIGNUP] Provisionamento concluído. Aguardando AppInitializer confirmar navegação.');
    } else {
      updateBootstrapOverlay(DEFAULT_BOOTSTRAP_MESSAGE, DEFAULT_BOOTSTRAP_HINT);
    }
  };

  const showBootstrapErrorOptions = (message: string) => {
    const formattedMessage = `${message}\nVocê pode tentar novamente ou preencher os dados manualmente.`;
    if (Platform.OS !== 'android') {
      showAlert('Erro no cadastro', formattedMessage, [
        { text: 'Preencher manualmente', onPress: handleGoToManualRegister },
        { text: 'Tentar novamente' },
      ]);
    } else {
      Alert.alert('Erro no cadastro', formattedMessage, [
        { text: 'Tentar novamente' },
        { text: 'Preencher manualmente', onPress: handleGoToManualRegister },
      ]);
    }
  };

  const executeDirectBootstrapFlow = async (
    userId: string,
    digits: string | null,
    metadataPayload: Record<string, string>
  ) => {
    logger.info('[DIRECT-SIGNUP] Disparando edge bootstrap-direct-signup', {
      userId,
      hasPhone: !!digits,
    });
    try {
      updateBootstrapOverlay('Provisionando sua empresa e templates...', DEFAULT_BOOTSTRAP_HINT);
      await runDirectSignupBootstrap({
        userId,
        email,
        telefone: digits,
        metadata: metadataPayload,
        clientPlatform: Platform.OS === 'ios'
          ? 'ios'
          : Platform.OS === 'android'
            ? 'android'
            : 'web',
      });
      updateBootstrapOverlay('Sincronizando seus dados iniciais...', DEFAULT_BOOTSTRAP_HINT);
      finishDirectBootstrapTracking(true);
    } catch (bootstrapError: any) {
      const message = bootstrapError?.message ?? 'Falha ao configurar sua conta automaticamente.';
      logger.error('Falha ao executar bootstrap direto', bootstrapError);
      finishDirectBootstrapTracking(false, message);
      showBootstrapErrorOptions(message);
      throw bootstrapError;
    }
  };

  // Configura deep links apenas no mobile
  useEffect(() => {
    logger.debug('Setup de listener para mudanças de autenticação');
    
    // Adicionar um listener para mudanças na sessão do usuário para debugging
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logger.debug(`Auth state changed: ${event}, ${session ? 'Session exists' : 'No session'}`);
      
      if (session) {
        logger.debug(`Usuário autenticado: ${session.user.id}`);
      }
    });
    
    return () => {
      subscription.unsubscribe();
      logger.debug('Cleanup de listener de autenticação');
    };
  }, []);

  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg);
    }
  };

  // Validar email
  const validateEmail = (text: string): boolean => {
    if (!text) {
      setEmailError('Campo obrigatório');
      return false;
    } else if (!/\S+@\S+\.\S+/.test(text)) {
      setEmailError('Email inválido');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const validatePhone = (text: string): boolean => {
    if (!isDirectSignUpFlow) {
      return true;
    }

    const digits = text.replace(/\D/g, '');
    if (!digits) {
      setPhoneError('Campo obrigatório');
      return false;
    }

    if (digits.length < 10) {
      setPhoneError('Informe um telefone válido com DDD');
      return false;
    }

    setPhoneError(null);
    return true;
  };

  // Validar senha
  const validatePassword = (text: string): boolean => {
    if (!text) {
      setPasswordError('Campo obrigatório');
      return false;
    } else if (text.length < 6) {
      setPasswordError('Senha deve ter pelo menos 6 caracteres');
      return false;
    }
    setPasswordError(null);
    
    // Se já existe confirmação de senha, validá-la também
    if (confirmPassword) {
      validateConfirmPassword(confirmPassword, text);
    }
    
    return true;
  };

  // Validar confirmação de senha
  const validateConfirmPassword = (text: string, pass: string = password): boolean => {
    if (!text) {
      setConfirmPasswordError('Campo obrigatório');
      return false;
    } else if (text !== pass) {
      setConfirmPasswordError('As senhas não coincidem');
      return false;
    }
    setConfirmPasswordError(null);
    return true;
  };

  // Validar todos os campos do formulário
  const validateForm = (): boolean => {
    const isEmailValid = validateEmail(email);
    const isPhoneValid = isDirectSignUpFlow ? validatePhone(phone) : true;
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);
    
    return isEmailValid && isPhoneValid && isPasswordValid && isConfirmPasswordValid;
  };

  const handleSignUp = async () => {
    logger.info('Iniciando processo de signup');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setSuccessMessage(null);
    setFormError(null);
    const phoneDigits = getPhoneDigits();

    try {
      const appVersion = Constants.expoConfig?.version ?? 'unknown';
      const { platformInfo, metadata } = getSignupClientMetadata(appVersion);
      logger.info('Metadados do signup', {
        platform: platformInfo.platform,
        os: platformInfo.os,
        appVersion,
        deviceType: metadata.device_type ?? null,
        deviceModelName: metadata.device_model_name ?? null,
        isDevice: metadata.device_is_device ?? null,
      });

      if (isDirectSignUpFlow && phoneDigits) {
        metadata.telefone = phoneDigits;
      }

      const signUpOptions: Parameters<typeof supabase.auth.signUp>[0]['options'] = {
        data: metadata,
      };

      if (useRedirectSignUpFlow) {
        const redirectTo = getURL();
        logger.info(`Realizando signup com redirectTo: ${redirectTo}`);
        signUpOptions.emailRedirectTo = redirectTo;
      } else {
        logger.info('Realizando signup com fluxo direto (sem redirect)');
        startDirectBootstrapTracking();
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: signUpOptions,
      });

      if (error) {
        logger.error('Erro no signup:', error);
        if (isDirectSignUpFlow) {
          finishDirectBootstrapTracking(false, error.message);
        }

        if (
          error.message?.toLowerCase().includes('email already registered') ||
          error.message?.toLowerCase().includes('email already')
        ) {
          logger.warn('Tentativa de cadastro com email já registrado', { email });
          showMessage('Erro no cadastro', 'Este email já está cadastrado. Por favor, faça login.');
          setFormError('Este email já está cadastrado. Por favor, faça login.');
        } else {
          showMessage('Erro no cadastro', error.message);
          setFormError(error.message);
        }
        return;
      }

      if (data?.user && data.user.identities && data.user.identities.length === 0) {
        logger.warn('Usuário já cadastrado (identities vazio)', { email });
        showMessage('Erro no cadastro', 'Este email já está cadastrado. Por favor, faça login.');
        setFormError('Este email já está cadastrado. Por favor, faça login.');
        if (isDirectSignUpFlow) {
          finishDirectBootstrapTracking(false, 'Usuário já existente.');
        }
        return;
      }

      if (useRedirectSignUpFlow) {
        logger.info('Signup realizado com sucesso, aguardando confirmação de email');
        showMessage('Cadastro enviado!', 'Verifique seu e-mail para confirmar a conta.');
        setSuccessMessage('Cadastro enviado! Verifique seu e-mail para confirmar a conta.');
      } else if (data?.session && data.user?.id) {
        logger.info('Signup direto concluído com sessão ativa');
        await executeDirectBootstrapFlow(data.user.id, phoneDigits || null, metadata);
      } else {
        logger.warn('Fluxo direto não retornou sessão; caindo para instrução de verificação por segurança');
        showMessage('Cadastro enviado!', 'Não conseguimos ativar automaticamente. Verifique seu e-mail para confirmar.');
        setSuccessMessage('Cadastro enviado! Verifique seu e-mail para confirmar.');
        if (isDirectSignUpFlow) {
          finishDirectBootstrapTracking(false, 'Sessão não retornada pelo Supabase.');
        }
      }

      setEmail('');
      setPhone('');
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      logger.error('Exceção no signup:', err);
      showMessage('Erro inesperado', err.message);
      setFormError(err.message);
      if (isDirectSignUpFlow) {
        finishDirectBootstrapTracking(false, err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Quando o usuário digita, validamos em tempo real
  const handleEmailChange = (text: string) => {
    setEmail(text);
    validateEmail(text);
    setFormError(null);
  };

  const handlePhoneChange = (text: string) => {
    setPhone(text);
    if (isDirectSignUpFlow) {
      validatePhone(text);
    }
    setFormError(null);
  };

  const handlePasswordChange = (text: string) => {
    setPassword(text);
    validatePassword(text);
    setFormError(null);
  };

  useEffect(() => {
    if (
      isDirectSignUpFlow &&
      directSignupState.awaitingAppReady &&
      checkUserDatabaseDone &&
      existsInDatabase
    ) {
      logger.info('[DIRECT-SIGNUP] AppInitializer confirmou usuário no banco. Removendo overlay local.');
      userActions.setDirectSignupBootstrapState({
        awaitingAppReady: false,
        overlayMessage: DEFAULT_BOOTSTRAP_MESSAGE,
        overlayHint: DEFAULT_BOOTSTRAP_HINT,
      });
    }
  }, [
    isDirectSignUpFlow,
    directSignupState.awaitingAppReady,
    checkUserDatabaseDone,
    existsInDatabase,
  ]);

  const handleConfirmPasswordChange = (text: string) => {
    setConfirmPassword(text);
    validateConfirmPassword(text);
    setFormError(null);
  };

  return (
    <View style={styles.container}>
      {/* Overlay de carregamento */}
      <WebLoadingOverlay 
        visible={Platform.OS === 'web' && (loading || shouldBlockForBootstrap)} 
        message={shouldBlockForBootstrap ? displayedOverlayMessage : 'Criando sua conta... Por favor, aguarde.'}
        hint={shouldBlockForBootstrap ? overlayHint : undefined}
        progress={shouldBlockForBootstrap ? displayedOverlayProgress : undefined}
        progressLabel={shouldBlockForBootstrap ? displayedOverlayProgressLabel : undefined}
      />
      {shouldBlockForBootstrap && Platform.OS !== 'web' && (
        <Modal transparent animationType="fade" visible>
          <View style={styles.bootstrapOverlay}>
            <View style={styles.bootstrapCard}>
              <ActivityIndicator size="large" color={theme.colors.primary.default} />
              {displayedOverlayProgress !== undefined && (
                <>
                  <View
                    style={styles.bootstrapProgressTrack}
                    accessibilityLabel={`Progresso ${displayedOverlayProgress}%`}
                  >
                    <View
                      style={[
                        styles.bootstrapProgressFill,
                        { width: `${displayedOverlayProgress}%` },
                      ]}
                    />
                  </View>
                  <Text style={styles.bootstrapProgressLabel}>{displayedOverlayProgressLabel}</Text>
                </>
              )}
              <Text style={styles.bootstrapText}>{displayedOverlayMessage}</Text>
              <Text style={styles.bootstrapHint}>{overlayHint}</Text>
            </View>
          </View>
        </Modal>
      )}
      
      <Text style={styles.title}>Criar Conta</Text>

      <View style={styles.inputContainer}>
        <Ionicons 
          name="mail-outline" 
          size={20} 
          color={emailError ? theme.colors.util.error : theme.colors.text.secondary} 
          style={styles.inputIcon}
        />
        <TextInput
          placeholder="E-mail"
          placeholderTextColor={theme.colors.text.tertiary}
          value={email}
          onChangeText={handleEmailChange}
          keyboardType="email-address"
          autoCapitalize="none"
          style={[styles.input, emailError ? styles.inputError : null]}
        />
      </View>
      <FormFieldError message={emailError} />

      {isDirectSignUpFlow && (
        <>
          <View style={styles.inputContainer}>
            <Ionicons
              name="call-outline"
              size={20}
              color={phoneError ? theme.colors.util.error : theme.colors.text.secondary}
              style={styles.inputIcon}
            />
            <FormattedInput
              placeholder="Telefone com DDD"
              placeholderTextColor={theme.colors.text.tertiary}
              value={phone}
              onChangeText={handlePhoneChange}
              type="phone"
              style={[styles.input, phoneError ? styles.inputError : null]}
            />
          </View>
          <FormFieldError message={phoneError} />
        </>
      )}

      <View style={styles.inputContainer}>
        <Ionicons 
          name="lock-closed-outline" 
          size={20} 
          color={passwordError ? theme.colors.util.error : theme.colors.text.secondary} 
          style={styles.inputIcon}
        />
        <TextInput
          placeholder="Senha"
          placeholderTextColor={theme.colors.text.tertiary}
          value={password}
          onChangeText={handlePasswordChange}
          secureTextEntry={!showPassword}
          style={[styles.input, passwordError ? styles.inputError : null, { flex: 1 }]}
        />
        <TouchableOpacity 
          style={styles.eyeIcon}
          onPress={() => setShowPassword(!showPassword)}
        >
          <Ionicons 
            name={showPassword ? "eye-off-outline" : "eye-outline"} 
            size={22} 
            color={theme.colors.text.secondary} 
          />
        </TouchableOpacity>
      </View>
      <FormFieldError message={passwordError} />

      <View style={styles.inputContainer}>
        <Ionicons 
          name="lock-closed-outline" 
          size={20} 
          color={confirmPasswordError ? theme.colors.util.error : theme.colors.text.secondary} 
          style={styles.inputIcon}
        />
        <TextInput
          placeholder="Confirmar Senha"
          placeholderTextColor={theme.colors.text.tertiary}
          value={confirmPassword}
          onChangeText={handleConfirmPasswordChange}
          secureTextEntry={!showConfirmPassword}
          style={[styles.input, confirmPasswordError ? styles.inputError : null, { flex: 1 }]}
        />
        <TouchableOpacity 
          style={styles.eyeIcon}
          onPress={() => setShowConfirmPassword(!showConfirmPassword)}
        >
          <Ionicons 
            name={showConfirmPassword ? "eye-off-outline" : "eye-outline"} 
            size={22} 
            color={theme.colors.text.secondary} 
          />
        </TouchableOpacity>
      </View>
      <FormFieldError message={confirmPasswordError} />

      {formError && (
        <View style={styles.formErrorContainer}>
          <Ionicons name="alert-circle" size={18} color={theme.colors.util.error} />
          <Text style={styles.formErrorText}>{formError}</Text>
        </View>
      )}

      <TouchableOpacity
        onPress={handleSignUp}
        disabled={loading}
        style={[
          styles.button,
          loading && styles.buttonDisabled
        ]}
      >
        {loading && Platform.OS !== 'web' ? (
          <ActivityIndicator color={theme.colors.text.inverse} />
        ) : (
          <View style={styles.buttonContent}>
            <Ionicons name="person-add" size={18} color={theme.colors.text.inverse} />
            <Text style={styles.buttonText}>Criar Conta</Text>
          </View>
        )}
      </TouchableOpacity>

      {successMessage && (
        <View style={styles.successContainer}>
          <Ionicons name="checkmark-circle" size={18} color={theme.colors.util.success} />
          <Text style={styles.successMessage}>{successMessage}</Text>
        </View>
      )}

      {isDirectSignUpFlow && (
        <TouchableOpacity 
          onPress={handleGoToManualRegister} 
          style={styles.manualLinkContainer}
          disabled={shouldBlockForBootstrap}
        >
          <Ionicons name="construct-outline" size={16} color={theme.colors.primary.default} />
          <Text style={styles.manualLinkText}>Preferir preencher os dados manualmente?</Text>
        </TouchableOpacity>
      )}

      <TouchableOpacity 
        onPress={() => router.replace('/login')} 
        style={styles.linkContainer}
      >
        <Ionicons name="log-in-outline" size={16} color={theme.colors.primary.default} />
        <Text style={styles.link}>Já tem conta? Entrar</Text>
      </TouchableOpacity>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: theme.spacing.lg,
    justifyContent: 'center',
    backgroundColor: theme.colors.background.primary,
  },
  title: {
    fontSize: theme.fontSizes.xxl,
    fontWeight: theme.fontWeights.bold as any,
    marginBottom: theme.spacing.xl,
    color: theme.colors.text.primary,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.tertiary,
    marginBottom: theme.spacing.xs,
  },
  inputIcon: {
    marginLeft: theme.spacing.md,
  },
  eyeIcon: {
    padding: theme.spacing.md,
  },
  input: {
    height: 50,
    paddingHorizontal: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    flex: 1,
  },
  inputError: {
    borderColor: theme.colors.util.error,
  },
  button: {
    height: 50,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.primary.default,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing.md,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    marginLeft: theme.spacing.xs,
  },
  formErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: theme.spacing.sm,
    justifyContent: 'center',
  },
  formErrorText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.md,
    marginLeft: theme.spacing.xs,
  },
  successContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.md,
    justifyContent: 'center',
  },
  successMessage: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.util.success,
    marginLeft: theme.spacing.xs,
  },
  linkContainer: {
    marginTop: theme.spacing.xl,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  link: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.md,
    marginLeft: theme.spacing.xs,
  },
  bootstrapOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: theme.zIndex.overlay,
  },
  bootstrapCard: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.xl,
    width: '80%',
    maxWidth: 320,
    alignItems: 'center',
  },
  bootstrapText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    textAlign: 'center',
  },
  bootstrapHint: {
    marginTop: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  bootstrapProgressTrack: {
    width: '100%',
    height: 10,
    marginTop: theme.spacing.lg,
    backgroundColor: theme.colors.background.tertiary,
    borderRadius: theme.borderRadius.circular,
    overflow: 'hidden',
  },
  bootstrapProgressFill: {
    height: '100%',
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.circular,
  },
  bootstrapProgressLabel: {
    marginTop: theme.spacing.sm,
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    textAlign: 'center',
  },
  manualLinkContainer: {
    marginTop: theme.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  manualLinkText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    marginLeft: theme.spacing.xs,
  },
});

export default SignUpScreen; 
